/**
 * Left and Right Block JavaScript
 * LCP Optimized: Deferred animations + lazy video loading
 */

(function() {
    'use strict';

    // Initialize block functionality
    function initLeftAndRight() {
        const blocks = document.querySelectorAll('.left-and-right');

        blocks.forEach(block => {

            // Add GSAP animations after content is visible
            initDeferredAnimations(block);

            // Initialize smooth scroll for scroll indicator
            initSmoothScroll(block);

            // Initialize stat counter animations
            initStatCounters(block);

            // Initialize video modal (hosted file only)
            initVideoModal(block);

            // Inline YouTube/embed: custom poster image, load iframe on play
            initInlineVideoEmbed(block);

            console.log('[BLOCK] left-and-right block initialized (LCP optimized)');
        });
    }

    /**
     * Animate stat numbers with ticker effect
     */
    function initStatCounters(block) {
        const statNumbers = block.querySelectorAll('.stat-number .number');
        if (statNumbers.length === 0) return;

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && !entry.target.classList.contains('counted')) {
                    entry.target.classList.add('counted');
                    animateCounter(entry.target);
                }
            });
        }, {
            threshold: 0.5,
            rootMargin: '0px'
        });

        statNumbers.forEach(num => observer.observe(num));
    }

    /**
     * Animate a single counter from 0 to target value
     */
    function animateCounter(element) {
        const targetText = element.textContent.trim();
        const hasComma = targetText.includes(',');
        const hasDecimal = targetText.includes('.');
        
        // Extract numeric value
        const targetValue = parseFloat(targetText.replace(/,/g, ''));
        if (isNaN(targetValue)) return;

        // Determine decimal places
        const decimalPlaces = hasDecimal ? (targetText.split('.')[1] || '').length : 0;
        
        const duration = 2000; // 2 seconds
        const steps = 60;
        const increment = targetValue / steps;
        const stepDuration = duration / steps;
        
        let current = 0;
        let step = 0;

        const timer = setInterval(() => {
            step++;
            current += increment;
            
            if (step >= steps) {
                current = targetValue;
                clearInterval(timer);
            }

            // Format the number
            let displayValue = current.toFixed(decimalPlaces);
            
            // Add commas if original had them
            if (hasComma) {
                displayValue = parseFloat(displayValue).toLocaleString('en-US', {
                    minimumFractionDigits: decimalPlaces,
                    maximumFractionDigits: decimalPlaces
                });
            }

            element.textContent = displayValue;
        }, stepDuration);
    }

    /**
     * Inline embed with poster: play injects iframe (autoplay) and hides poster.
     */
    function initInlineVideoEmbed(block) {
        const wrapper = block.querySelector('.image-wrapper--inline-embed-poster');
        if (!wrapper) return;

        const playBtn = wrapper.querySelector('.video-play-btn--inline-embed');
        const poster = wrapper.querySelector('.hero-inline-embed__poster');
        const player = wrapper.querySelector('.hero-inline-embed__player');
        if (!playBtn || !poster || !player) return;

        const baseSrc = playBtn.getAttribute('data-embed-src');
        if (!baseSrc) return;

        playBtn.addEventListener('click', () => {
            let playUrl;
            try {
                playUrl = new URL(baseSrc, window.location.href);
            } catch (e) {
                return;
            }
            playUrl.searchParams.set('autoplay', '1');

            const iframe = document.createElement('iframe');
            iframe.setAttribute('src', playUrl.toString());
            iframe.setAttribute('title', 'Video');
            iframe.setAttribute('allowfullscreen', '');
            iframe.setAttribute(
                'allow',
                'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
            );
            iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');

            player.textContent = '';
            player.appendChild(iframe);
            player.removeAttribute('hidden');
            player.setAttribute('aria-hidden', 'false');
            poster.setAttribute('hidden', '');
            poster.setAttribute('aria-hidden', 'true');
            playBtn.setAttribute('tabindex', '-1');
            playBtn.setAttribute('aria-hidden', 'true');
        });
    }

    /**
     * Video modal - play button opens modal, close stops video
     */
    function initVideoModal(block) {
        const modal = block.querySelector('.video-modal');
        if (!modal) return;

        const playBtn = block.querySelector('.video-play-btn:not(.video-play-btn--inline-embed)');
        if (!playBtn) return;

        const video = modal.querySelector('.video-modal__video');
        const backdrop = modal.querySelector('.video-modal__backdrop');
        const closeBtn = modal.querySelector('.video-modal__close');

        function openModal() {
            modal.removeAttribute('hidden');
            modal.setAttribute('data-open', 'true');
            modal.setAttribute('aria-hidden', 'false');
            if (video) video.play();
            document.body.style.overflow = 'hidden';
            modal.setAttribute('tabindex', '-1');
            modal.focus();
            document.dispatchEvent(new CustomEvent('modalOpen'));
        }

        function closeModal() {
            modal.setAttribute('hidden', '');
            modal.removeAttribute('data-open');
            modal.setAttribute('aria-hidden', 'true');
            if (video) {
                video.pause();
                video.currentTime = 0;
            }
            document.body.style.overflow = '';
            document.dispatchEvent(new CustomEvent('modalClose'));
        }

        playBtn.addEventListener('click', openModal);
        if (closeBtn) closeBtn.addEventListener('click', closeModal);
        if (backdrop) backdrop.addEventListener('click', closeModal);

        modal.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && modal.getAttribute('data-open') === 'true') {
                closeModal();
            }
        });
    }

    /**
     * Smooth scroll to next section when scroll indicator is clicked
     */
    function initSmoothScroll(block) {
        const scrollIndicator = block.querySelector('.scroll-indicator');
        if (!scrollIndicator) return;

        scrollIndicator.addEventListener('click', function(e) {
            e.preventDefault();

            // Find the next section after this block
            const nextElement = block.nextElementSibling;

            if (nextElement) {
                nextElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
            } else {
                // If no next element, scroll down one viewport
                window.scrollBy({
                    top: window.innerHeight,
                    behavior: 'smooth'
                });
            }

            console.log('[BLOCK] Smooth scroll triggered');
        });
    }

    /**
     * Add GSAP animations after LCP to not block rendering
     */
    function initDeferredAnimations(block) {
        // Function to run animations
        function runAnimations() {
            // Get animation elements
            const hallmarkWrapper = block.querySelector('.hallmark-wrapper');
            const hallmarkLogo = block.querySelector('.hallmark-logo');

            // Set initial states
            if (hallmarkWrapper) {
                gsap.set(hallmarkWrapper, { opacity: 0, force3D: false });
            }

            // Create GSAP timeline
            const tl = gsap.timeline({
                defaults: { ease: 'power3.out' }
            });

            // 1. Hallmark SVG drawing animation using DrawSVGPlugin
            if (hallmarkLogo && hallmarkWrapper) {
                const isSVG = hallmarkLogo.tagName === 'svg' || hallmarkLogo.querySelector('svg');
                console.log('[BLOCK] Hallmark found:', hallmarkLogo);
                console.log('[BLOCK] Is SVG:', isSVG);

                if (isSVG) {
                    const svg = hallmarkLogo.tagName === 'svg' ? hallmarkLogo : hallmarkLogo.querySelector('svg');
                    const paths = svg.querySelectorAll('path, circle, rect, line, polyline, polygon, ellipse');
                    console.log('[BLOCK] SVG paths found:', paths.length);
                    console.log('[BLOCK] DrawSVGPlugin available:', typeof window.DrawSVGPlugin);

                    // Fade in wrapper
                    tl.to(hallmarkWrapper, {
                        opacity: 1,
                        force3D: false,
                        duration: 0.3
                    }, 0);

                    // Draw SVG paths using DrawSVGPlugin
                    if (paths.length > 0) {
                        // Check if DrawSVGPlugin is actually registered
                        if (typeof window.DrawSVGPlugin !== 'undefined') {
                            console.log('[BLOCK] Using DrawSVGPlugin');

                            // Set initial states BEFORE animation to prevent flash
                            gsap.set(paths, {
                                drawSVG: '0%',
                                fillOpacity: 0
                            });

                            // Draw the stroke to 100%, then fade in fill
                            tl.to(paths, {
                                drawSVG: '100%',
                                duration: 1.5,
                                ease: 'power2.inOut',
                                stagger: 0.1
                            }, 0.2)
                            // Then fade in the fill (starts when stroke is almost done)
                            .to(paths, {
                                fillOpacity: 1,
                                duration: 0.6,
                                ease: 'power2.out'
                            }, '-=0.3');
                        } else {
                            console.warn('[BLOCK] DrawSVGPlugin not available, using fallback strokeDashoffset animation');
                            // Fallback to free stroke animation
                            paths.forEach(path => {
                                const length = path.getTotalLength ? path.getTotalLength() : 0;
                                if (length) {
                                    path.style.strokeDasharray = length;
                                    path.style.strokeDashoffset = length;
                                }
                            });

                            tl.to(paths, {
                                strokeDashoffset: 0,
                                duration: 1.5,
                                ease: 'power2.inOut',
                                stagger: 0.1
                            }, 0.2);
                        }
                    }
                } else {
                    // Image fallback: scale and fade effect
                    console.log('[BLOCK] Not SVG, using scale animation');
                    gsap.set(hallmarkLogo, { scale: 0.8 });

                    tl.to(hallmarkWrapper, {
                        opacity: 1,
                        force3D: false,
                        duration: 0.5
                    }, '-=0.3')
                    .to(hallmarkLogo, {
                        scale: 1,
                        duration: 0.5,
                        ease: 'back.out(1.7)'
                    }, '-=0.3');
                }
            }

            // Animate other content elements with their individual delays
            const contentElements = block.querySelectorAll('[data-gsap-animate]:not([data-gsap-animate="stat-reveal"])');
            if (contentElements.length > 0) {
                contentElements.forEach(element => {
                    const delay = parseFloat(element.getAttribute('data-gsap-delay')) || 0;
                    
                    tl.to(element, {
                        opacity: 1,
                        y: 0,
                        duration: 0.6,
                        ease: 'power2.out'
                    }, delay / 1000); // Convert milliseconds to seconds
                });
            }

            console.log('[BLOCK] Left and Right animations complete');
        }

        // Use Intersection Observer to trigger animations when block enters viewport
        function setupScrollTrigger() {
            const observerOptions = {
                root: null,
                rootMargin: '0px',
                threshold: 0.2 // Trigger when 20% of block is visible
            };

            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        // Block is in viewport, run animations
                        runAnimations();
                        // Disconnect observer after first trigger
                        observer.disconnect();
                        console.log('[BLOCK] Main content animations triggered by scroll');
                    }
                });
            }, observerOptions);

            observer.observe(block);
        }

        // Wait for GSAP to be available, then setup scroll trigger
        if (typeof gsap !== 'undefined') {
            setupScrollTrigger();
        } else {
            // Poll for GSAP with timeout
            let attempts = 0;
            const maxAttempts = 50; // 5 seconds max
            const checkGSAP = setInterval(() => {
                attempts++;
                if (typeof gsap !== 'undefined') {
                    clearInterval(checkGSAP);
                    setupScrollTrigger();
                } else if (attempts >= maxAttempts) {
                    clearInterval(checkGSAP);
                    console.log('[BLOCK] GSAP not available after timeout, skipping animations');
                }
            }, 100);
        }
    }

    // Initialize on DOM ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initLeftAndRight);
    } else {
        initLeftAndRight();
    }
})();
